import React, { useState } from 'react';
import styled from 'styled-components';

import colors, { withOpacity } from '@front/common/src/utils/colors';
import { getCurrentCountry, isCountryGB } from '@front/common/src/utils/country';
import { COUNTRIES } from '@front/common/src/constants/country';
import { Country } from '@front/common/src/types/commons';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Title from '@front/web-ui/src/components/base-text/variants/title';
import Icon from '@front/web-ui/src/components/icon/icon';
import Modal from '@front/web-ui/src/components/modal/modal';
import Alert from '@front/web-ui/src/components/alert/alert';

import { useAnimatedIncrementedValue } from '../../hooks/use-animated-incremented-value';

const t = translate('screens.revenue_simulator.simulator_card');

const ColoredContainer = styled.div<{ isDisabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 12px;
  padding: 20px;
  margin-top: 25px;
  background: linear-gradient(-90deg, ${colors.pink100} 0%, ${colors.primary100} 100%);
  height: 80px;

  ${({ isDisabled }: { isDisabled: boolean }) =>
    isDisabled &&
    `
    .opacity-on-disabled {
      opacity: 0.5;
    };
    background: linear-gradient(-90deg, ${withOpacity(colors.pink100, 0.5)} 0%, ${withOpacity(
      colors.primary100,
      0.5,
    )} 100%);
  `};
`;

const StyledIcon = styled(Icon)`
  opacity: 0.5;
  display: block;
`;

const StyledButton = styled.button`
  padding: 0;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const StyledPlaceholderText = styled(Text.MDMedium)`
  flex: 1;
`;

const StyledTextCard = styled(Text.SMBold)`
  letter-spacing: 0.6px;
`;

const StyledUnit = styled(Title.MD)`
  color: ${colors.white100};
`;

const StyledNumber = styled(Title.XL)`
  line-height: 1;
  color: ${colors.white100};
`;

const StyledLabel = styled(Text.SMMedium)`
  color: ${colors.white100};
`;
const PricingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
`;

const Content = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const StyledAlert = styled(Alert)`
  margin: 0 20px;
`;

const PAY_RATE_BY_COUNTRY = {
  [COUNTRIES.FR]: 18,
  [COUNTRIES.GB]: 12,
};

const payRate = (countryCode: Country) => PAY_RATE_BY_COUNTRY[countryCode];

type AnimatedNumberProps = {
  hours: number;
};
const AnimatedNumber = ({ hours }: AnimatedNumberProps) => {
  const revenues = (value: number) => Math.round(value * payRate(getCurrentCountry()) * 4.3);
  const count = useAnimatedIncrementedValue({
    from: 1,
    to: hours,
  });

  return <StyledNumber>{revenues(count)}</StyledNumber>;
};

type SimulationCardProps = {
  title: string;
  hours: string;
  isDisabled: boolean;
};

const SimulationCard = ({ title, hours, isDisabled }: SimulationCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const primaryButton = {
    children: t('.modal.close_modal'),
    onPress: () => setIsOpen(false),
  };

  return (
    <>
      <Modal isOpen={isOpen} title={title} onClose={() => setIsOpen(false)} primaryButton={primaryButton}>
        <StyledAlert type='info' content={t('.modal.info_1')} />
        <Content>
          <Text>{t('.modal.info_2')}</Text>
        </Content>
      </Modal>
      <ColoredContainer isDisabled={isDisabled}>
        <StyledButton type='button' aria-label='additional information' onClick={() => setIsOpen(true)}>
          <StyledIcon name='question' color={colors.white100} size={15} />
        </StyledButton>
        <StyledTextCard className='opacity-on-disabled'>{title}</StyledTextCard>
        {isDisabled ? (
          <PricingContainer>
            <div>
              <StyledPlaceholderText>{t('.availablities_choice')}</StyledPlaceholderText>
              <br />
              <StyledPlaceholderText>{t('.availabilities')}</StyledPlaceholderText>
            </div>
            <div data-testid='revenue-placeholder' className='opacity-on-disabled'>
              <StyledNumber>{t('.revenue_placeholder')}</StyledNumber>
              <StyledUnit>{t('.revenue_unit')}</StyledUnit>
            </div>
          </PricingContainer>
        ) : (
          <PricingContainer>
            <div>
              <StyledLabel>{t('.pay_rate_label')}</StyledLabel>
              <div>
                {/* We are doing this weird trick because the currency do not have the same size as the price
                so we cant use the formatPrice function */}
                {isCountryGB() && <StyledUnit>£</StyledUnit>}
                <StyledNumber>{payRate(getCurrentCountry())}</StyledNumber>
                <StyledUnit>{t('.pay_rate_unit')}</StyledUnit>
              </div>
            </div>
            <div>
              <StyledLabel>{t('.revenue_label')}</StyledLabel>
              <div>
                {/* We are doing this weird trick because the currency do not have the same size as the price
                so we cant use the formatPrice function */}
                {isCountryGB() && <StyledUnit>£</StyledUnit>}
                <AnimatedNumber hours={+hours} />
                <StyledUnit>{t('.revenue_unit')}</StyledUnit>
              </div>
            </div>
          </PricingContainer>
        )}
      </ColoredContainer>
    </>
  );
};

export default SimulationCard;
